import { VIDEO_CONSTRAINTS } from "../constants";
import type { StreamStatus } from "./mediaTypes";

export const STATUS_PRIORITY: StreamStatus[] = [
  "error",
  "requesting",
  "active",
  "idle",
];

export const stopAllTracks = (stream: MediaStream | null) => {
  stream?.getTracks().forEach(track => {
    if (track.readyState === "live") {
      track.stop();
    }
  });
};

export const removeAndStopTracks = (
  stream: MediaStream,
  kind: "audio" | "video",
) => {
  const tracks =
    kind === "audio" ? stream.getAudioTracks() : stream.getVideoTracks();
  tracks.forEach(track => {
    stream.removeTrack(track);
    if (track.readyState === "live") track.stop();
  });
};

export const replaceStreamTrack = (
  stream: MediaStream,
  kind: "audio" | "video",
  nextTrack: MediaStreamTrack,
) => {
  const currentTracks =
    kind === "audio" ? stream.getAudioTracks() : stream.getVideoTracks();
  const wasTrackEnabled = currentTracks[0]?.enabled ?? true;
  nextTrack.enabled = wasTrackEnabled;
  removeAndStopTracks(stream, kind);
  stream.addTrack(nextTrack);
};

export const cleanupScreenShare = (
  streamRef: React.MutableRefObject<MediaStream | null>,
  endedHandlerRef: React.MutableRefObject<(() => void) | null>,
) => {
  if (streamRef.current) {
    const videoTrack = streamRef.current.getVideoTracks()[0];
    if (videoTrack && endedHandlerRef.current) {
      videoTrack.removeEventListener("ended", endedHandlerRef.current);
      endedHandlerRef.current = null;
    }
    stopAllTracks(streamRef.current);
    streamRef.current = null;
  }
};

export const getAudioConstraint = (micDeviceId: string | null) =>
  micDeviceId ? { deviceId: { exact: micDeviceId } } : true;

export const getVideoConstraint = (cameraDeviceId: string | null) =>
  cameraDeviceId
    ? { deviceId: { exact: cameraDeviceId }, ...VIDEO_CONSTRAINTS }
    : VIDEO_CONSTRAINTS;

// Strips USB vendor:product hardware IDs (e.g. "(0fd9:0070)") that browsers
// append to device labels on some platforms, leaving only the human-readable name.
export const normalizeDeviceLabel = (label: string) =>
  label.replace(/ \([0-9a-f]{4}:[0-9a-f]{4}\)$/i, "");

export const getTrackEnabledState = (stream: MediaStream | null) => ({
  isCameraEnabled:
    stream?.getVideoTracks().some(track => track.enabled) ?? false,
  isMicEnabled: stream?.getAudioTracks().some(track => track.enabled) ?? false,
});

// Acquires a single track of the given kind via getUserMedia.
// Stops all other tracks on the temporary stream to release hardware immediately.
export const acquireSingleTrack = async (
  constraints: MediaStreamConstraints,
  kind: "audio" | "video",
  errorMessage: string,
): Promise<MediaStreamTrack> => {
  const tempStream = await navigator.mediaDevices.getUserMedia(constraints);
  const track =
    kind === "audio"
      ? tempStream.getAudioTracks()[0]
      : tempStream.getVideoTracks()[0];
  if (!track) {
    tempStream.getTracks().forEach(tr => tr.stop());
    throw new Error(errorMessage);
  }
  tempStream.getTracks().forEach(tr => {
    if (tr !== track) tr.stop();
  });
  return track;
};

export type StartStrategy =
  | "no-op"
  | "replace-mic"
  | "replace-camera"
  | "full-restart";

export const getStartStrategy = (
  hasStream: boolean,
  hasCameraChanged: boolean,
  hasMicChanged: boolean,
): StartStrategy => {
  if (!hasStream) return "full-restart";
  if (!hasCameraChanged && !hasMicChanged) return "no-op";
  if (hasMicChanged && !hasCameraChanged) return "replace-mic";
  if (hasCameraChanged && !hasMicChanged) return "replace-camera";
  return "full-restart";
};
